import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Save, ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_units,
  get_view_units,
} from "../ServiceConnection/serviceconnection";
import Loader from "../components/layout/Loader";

export default function AddUnit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const primary_id = location.state?.id;

  // Refs for form inputs
  const unitNameRef = useRef(null);
  const shortCodeRef = useRef(null);
  const baseUnitRef = useRef(null);
  const conversionRef = useRef(null);

  const [showLoaderAdmin, setshowLoaderAdmin] = useState(false);
  const [unitName, setUnitName] = useState("");
  const [shortCode, setShortCode] = useState("");
  const [isLinkToBase, setIsLinkToBase] = useState(false);
  const [baseUnit, setBaseUnit] = useState("");
  const [conversionToBase, setConversionToBase] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [baseUnits, setBaseUnits] = useState([]);

  // Error states
  const [unitNameError, setUnitNameError] = useState("");
  const [shortCodeError, setShortCodeError] = useState("");
  const [baseUnitError, setBaseUnitError] = useState("");
  const [conversionError, setConversionError] = useState("");

  // Fetch base units on mount
  useEffect(() => {
    master_data_get();
  }, []);

  const master_data_get = async () => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");

    try {
      const response = await server_post_data(get_view_units, fd);
      if (!response.data.error && response.data.message) {
        const apiUnits = response.data.message;

        // Filter only base units (base_unit_id == 0 or null)
        const onlyBaseUnits = apiUnits.filter(
          (item) =>
            !item.base_unit_id ||
            item.base_unit_id === "0" ||
            item.base_unit_id === 0,
        );

        setBaseUnits(onlyBaseUnits);
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to load units",
        variant: "destructive",
      });
    } finally {
      setshowLoaderAdmin(false);
    }
  };

  const handleSaveChanges = async () => {
    let isValid = true;
    let firstErrorField = null;

    // Validation
    if (!unitName.trim()) {
      setUnitNameError("Unit Name is required!");
      isValid = false;
      if (!firstErrorField) firstErrorField = unitNameRef;
    } else {
      setUnitNameError("");
    }

    if (!shortCode.trim()) {
      setShortCodeError("Short Code is required!");
      isValid = false;
      if (!firstErrorField) firstErrorField = shortCodeRef;
    } else if (shortCode.length > 20) {
      setShortCodeError("Short Code must be max 20 characters!");
      isValid = false;
      if (!firstErrorField) firstErrorField = shortCodeRef;
    } else {
      setShortCodeError("");
    }

    if (isLinkToBase && !baseUnit) {
      setBaseUnitError("Base Unit is required when linking to base unit!");
      isValid = false;
      if (!firstErrorField) firstErrorField = baseUnitRef;
    } else {
      setBaseUnitError("");
    }

    if (isLinkToBase && !conversionToBase) {
      setConversionError(
        "Conversion value is required when linking to base unit!",
      );
      isValid = false;
      if (!firstErrorField) firstErrorField = conversionRef;
    } else {
      setConversionError("");
    }

    // Focus on first error field
    if (!isValid && firstErrorField?.current) {
      firstErrorField.current.focus();
      // Smooth scroll to the error field
      firstErrorField.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    setshowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("unit_name", unitName.trim());
    fd.append("short_code_name", shortCode.trim().toUpperCase());
    fd.append("base_unit_id", isLinkToBase ? baseUnit || "0" : "0");
    fd.append(
      "conversion_to_base_unit",
      isLinkToBase ? conversionToBase || "0" : "0",
    );
    fd.append("active_status", isActive ? "1" : "0");

    try {
      const response = await server_post_data(save_update_units, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to save unit",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: primary_id
            ? "Unit updated successfully!"
            : "Unit created successfully!",
        });
        navigate("/view-units");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setshowLoaderAdmin(false);
    }
  };

  const handleShortCodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    if (value.length <= 20) {
      setShortCode(value);
    }
  };

  const selectedBaseUnitData = baseUnits.find((u) => u.primary_id == baseUnit);

  return (
    <MainLayout>
      <Header
        title={primary_id ? "Edit Unit" : "Add New Unit"}
        subtitle="Create and manage measurement units"
      />

      <div className="p-6 space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/view-units")}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Units
        </Button>

        <div className="space-y-6">
          {/* Basic Information Card */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>
                Enter the fundamental details for this measurement unit
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Unit Name */}
                <div className="space-y-2">
                  <Label htmlFor="unitName">
                    Unit Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    ref={unitNameRef}
                    id="unitName"
                    placeholder="e.g., Gram, Kilogram, Liter"
                    value={unitName}
                    onChange={(e) => setUnitName(e.target.value)}
                    className={unitNameError ? "border-destructive" : ""}
                  />
                  {unitNameError && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {unitNameError}
                    </p>
                  )}
                </div>

                {/* Short Code */}
                <div className="space-y-2">
                  <Label htmlFor="shortCode">
                    Short Code <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    ref={shortCodeRef}
                    id="shortCode"
                    placeholder="e.g., g, kg, L"
                    value={shortCode}
                    onChange={handleShortCodeChange}
                    maxLength={10}
                    className={shortCodeError ? "border-destructive" : ""}
                  />
                  {shortCodeError && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {shortCodeError}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Max 10 characters, auto-uppercase
                  </p>
                </div>

                {/* Active Status */}
                <div className="space-y-2">
                  <Label htmlFor="isActive">Status</Label>
                  <div className="flex items-center gap-3 h-10">
                    <Switch
                      id="isActive"
                      checked={isActive}
                      onCheckedChange={setIsActive}
                      className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-cyan-500 data-[state=checked]:to-teal-600"
                    />
                    <span className="text-sm font-medium">
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Toggle to activate/deactivate
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Unit Type Configuration Card */}
          <Card>
            <CardHeader>
              <CardTitle>Unit Type Configuration</CardTitle>
              <CardDescription>
                Configure how this unit relates to other units
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                <div className="space-y-1">
                  <Label htmlFor="linkToBase" className="text-base font-medium">
                    Link to Base Unit
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Enable to link this unit to an existing base unit for
                    conversion
                  </p>
                </div>
                <Switch
                  id="linkToBase"
                  checked={isLinkToBase}
                  onCheckedChange={setIsLinkToBase}
                  className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-cyan-500 data-[state=checked]:to-teal-600"
                />
              </div>
            </CardContent>
          </Card>

          {/* Base Unit Conversion Card - Conditional */}
          {isLinkToBase && (
            <div className="animate-in fade-in-50 duration-300">
              <Card className="border-cyan-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-cyan-500 to-teal-600 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    Base Unit Conversion
                  </CardTitle>
                  <CardDescription>
                    Define the conversion relationship with the base unit
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Base Unit Dropdown */}
                    <div className="space-y-2">
                      <Label htmlFor="baseUnit">
                        Base Unit <span className="text-destructive">*</span>
                      </Label>
                      <Select value={baseUnit} onValueChange={setBaseUnit}>
                        <SelectTrigger
                          ref={baseUnitRef}
                          className={baseUnitError ? "border-destructive" : ""}
                        >
                          <SelectValue placeholder="Select base unit" />
                        </SelectTrigger>
                        <SelectContent>
                          {baseUnits.map((unit) => (
                            <SelectItem
                              key={unit.primary_id}
                              value={unit.primary_id.toString()}
                            >
                              {unit.unit_name} ({unit.short_code_name})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {baseUnitError && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {baseUnitError}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Select the parent/base unit
                      </p>
                    </div>

                    {/* Conversion to Base Unit */}
                    <div className="space-y-2">
                      <Label htmlFor="conversion">
                        Conversion Factor{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        ref={conversionRef}
                        id="conversion"
                        placeholder="e.g., 1000"
                        value={conversionToBase}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*\.?\d*$/.test(value)) {
                            setConversionToBase(value);
                          }
                        }}
                        className={conversionError ? "border-destructive" : ""}
                      />
                      {conversionError && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {conversionError}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        E.g., 1 kg = 1000 g (enter 1000)
                      </p>
                    </div>

                    {/* Conversion Preview */}
                    <div className="space-y-2">
                      <Label>Conversion Preview</Label>
                      <div className="h-10 px-3 flex items-center rounded-md bg-green-50 border border-green-200 text-green-700 font-medium text-sm">
                        {baseUnit && conversionToBase
                          ? `${conversionToBase} ${
                              selectedBaseUnitData?.short_code_name || ""
                            } = 1 ${shortCode || "unit"}`
                          : "Enter values to see preview"}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Auto-calculated conversion display
                      </p>
                    </div>
                  </div>

                  {/* Example Alert */}
                  <Alert className="bg-cyan-50 border-cyan-200">
                    <AlertCircle className="h-4 w-4 text-cyan-600" />
                    <AlertDescription className="text-cyan-800">
                      <strong>Example:</strong> If creating "Kilogram" linked to
                      "Gram", enter 1000 as the conversion factor (1 kg = 1000
                      g)
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => navigate("/unit_master")}
              disabled={showLoaderAdmin}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={showLoaderAdmin}
              className="gap-2 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white"
            >
              {showLoaderAdmin ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {primary_id ? "Update Unit" : "Create Unit"}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      {showLoaderAdmin && <Loader />}
    </MainLayout>
  );
}
